/**
 * Google ADK Adapter — implements SdkAdapter for @google/adk (ADK-TS / adk-js)
 *
 * Replaces the raw @google/genai adapter. ADK provides a native subagent
 * mechanism (`LlmAgent.subAgents` + description-based agent transfer), which is
 * required for model B (LLM-internal routing): the entry agent's LLM delegates
 * to candidate sub-agents registered in the agent tree.
 *
 * Supports:
 * - send()          — build a root LlmAgent (+ candidate subAgents), run once,
 *                     return the final response text
 * - sendExecution() — rich path using full contract context
 *
 * No followUp(): ADK's InMemoryRunner uses ephemeral sessions, so the runtime's
 * recovery loop falls back to a fresh send() with reconstructed context.
 *
 * API used (verified against the installed package's type definitions under
 * node_modules/@google/adk/dist/types):
 *   - class LlmAgent (agents/llm_agent.d.ts): constructor(config) where config
 *     extends BaseAgentConfig { name; description?; subAgents?: BaseAgent[] }
 *     and adds { model?: string | BaseLlm; instruction?: string; tools? }.
 *   - class InMemoryRunner (runner/in_memory_runner.d.ts): constructor({ agent,
 *     appName? }); inherits runEphemeral({ userId, newMessage: Content })
 *     returning AsyncGenerator<Event> (runner/runner.d.ts).
 *   - isFinalResponse(event) / stringifyContent(event) (events/event.d.ts).
 *   - class Gemini (models/google_llm.d.ts): constructor({ model, apiKey })
 *     — used to inject an explicit API key; otherwise ADK reads
 *     GOOGLE_GENAI_API_KEY / GEMINI_API_KEY from the environment.
 */

import type {
  SdkAdapter,
  AdapterSendOptions,
  AgentExecutionRequest,
  AgentProgressEvent,
  CacheConfig,
  CandidateAgent,
} from "../lib/task-runner.js";
import type { GuardrailHooks } from "../lib/guardrail-hooks.js";
import type { MemoryRef } from "../lib/memory-store.js";

// ---------------------------------------------------------------------------
// Duck-typed SDK surface (avoid a hard compile-time dependency on @google/adk)
// ---------------------------------------------------------------------------

type AdkContentPart = {
  text?: string;
  functionCall?: { name?: string; args?: Record<string, unknown> };
  functionResponse?: { name?: string; response?: unknown };
};

type AdkContent = {
  role?: string;
  parts?: AdkContentPart[];
};

type AdkEvent = {
  author?: string;
  content?: AdkContent;
  [key: string]: unknown;
};

type LlmAgentConfig = {
  name: string;
  description?: string;
  model?: string | object;
  instruction?: string;
  tools?: unknown[];
  subAgents?: unknown[];
};

type LlmAgentInstance = object;
type LlmAgentConstructor = new (config: LlmAgentConfig) => LlmAgentInstance;

type RunnerInstance = {
  runEphemeral(params: {
    userId: string;
    newMessage: AdkContent;
    stateDelta?: Record<string, unknown>;
  }): AsyncGenerator<AdkEvent, void, unknown>;
};

type InMemoryRunnerConstructor = new (params: {
  agent: LlmAgentInstance;
  appName?: string;
}) => RunnerInstance;

type GeminiConstructor = new (params: { model?: string; apiKey?: string }) => object;

interface AdkSdk {
  LlmAgent: LlmAgentConstructor;
  InMemoryRunner: InMemoryRunnerConstructor;
  isFinalResponse: (event: AdkEvent) => boolean;
  stringifyContent: (event: AdkEvent) => string;
  Gemini: GeminiConstructor;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * ADK requires agent names to be valid JS identifiers, unique within the tree,
 * and not "user". DSL agent ids may contain hyphens, so sanitize to identifiers.
 */
function sanitizeAgentName(name: string): string {
  let id = name.replace(/[^a-zA-Z0-9_]/g, "_");
  if (id.length === 0 || /^[0-9]/.test(id)) id = `agent_${id}`;
  if (id === "user") id = "user_agent";
  return id;
}

/** Concatenate text parts from an event's content. */
function extractEventText(event: AdkEvent): string {
  const parts = event.content?.parts;
  if (!parts) return "";
  return parts.map((p) => p.text ?? "").join("");
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AdkSdkAdapterConfig {
  /** API key for the Gemini API. Falls back to GOOGLE_GENAI_API_KEY / GEMINI_API_KEY env. */
  apiKey?: string;
  /** Gemini model identifier (e.g. "gemini-2.5-flash", "gemini-2.5-pro"). */
  model?: string;
  /** Root agent name (must be a JS identifier). Defaults to "root_agent". */
  rootAgentName?: string;
  /** Runtime guardrail hooks. Reserved for ADK callback/plugin mapping (not yet wired). */
  guardrailHooks?: GuardrailHooks;
  /** Prompt caching configuration. Enabled by default. */
  cacheConfig?: CacheConfig;
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

export class AdkSdkAdapter implements SdkAdapter {
  private apiKey: string | undefined;
  private model: string;
  private rootAgentName: string;
  private guardrailHooks: GuardrailHooks | undefined;
  private cacheConfig: CacheConfig;
  private lastMemoryRef: MemoryRef | null = null;
  private runCounter = 0;

  private sdk: AdkSdk | null = null;

  constructor(config: AdkSdkAdapterConfig = {}) {
    this.apiKey = config.apiKey;
    this.model = config.model ?? "gemini-2.5-flash";
    this.rootAgentName = config.rootAgentName ?? "root_agent";
    this.guardrailHooks = config.guardrailHooks;
    this.cacheConfig = config.cacheConfig ?? { enabled: true };
  }

  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------

  private async resolveSdk(): Promise<AdkSdk> {
    if (this.sdk) return this.sdk;

    const mod = (await import("@google/adk")) as unknown as AdkSdk;
    this.sdk = {
      LlmAgent: mod.LlmAgent,
      InMemoryRunner: mod.InMemoryRunner,
      isFinalResponse: mod.isFinalResponse,
      stringifyContent: mod.stringifyContent,
      Gemini: mod.Gemini,
    };
    return this.sdk;
  }

  /** Resolve the `model` field for LlmAgent — a Gemini instance when an explicit
   *  API key is supplied, otherwise the bare model id (ADK reads env keys). */
  private resolveModel(sdk: AdkSdk): string | object {
    if (this.apiKey) {
      return new sdk.Gemini({ model: this.model, apiKey: this.apiKey });
    }
    return this.model;
  }

  /** Build the root LlmAgent with candidate sub-agents registered for transfer. */
  private buildRootAgent(
    sdk: AdkSdk,
    instruction: string,
    agents?: CandidateAgent[],
  ): LlmAgentInstance {
    const subAgents = (agents ?? []).map((a) => {
      const config: LlmAgentConfig = {
        name: sanitizeAgentName(a.name),
        description: a.description,
        instruction: a.prompt,
        model: a.model ?? this.resolveModel(sdk),
      };
      if (a.tools) config.tools = a.tools;
      return new sdk.LlmAgent(config);
    });

    const rootConfig: LlmAgentConfig = {
      name: sanitizeAgentName(this.rootAgentName),
      instruction,
      model: this.resolveModel(sdk),
    };
    if (subAgents.length > 0) rootConfig.subAgents = subAgents;

    return new sdk.LlmAgent(rootConfig);
  }

  private async runAgent(
    instruction: string,
    userMessage: string,
    agents: CandidateAgent[] | undefined,
    onProgress?: (event: AgentProgressEvent) => void,
  ): Promise<string> {
    const sdk = await this.resolveSdk();
    const root = this.buildRootAgent(sdk, instruction, agents);
    const runner = new sdk.InMemoryRunner({ agent: root });

    let finalText = "";
    const stream = runner.runEphemeral({
      userId: "agent-runtime",
      newMessage: { role: "user", parts: [{ text: userMessage }] },
    });

    for await (const event of stream) {
      if (onProgress) emitProgressFromAdkEvent(event, onProgress);

      const ev = event as Record<string, unknown>;
      if (ev.errorCode || ev.errorMessage) {
        throw new Error(
          `Gemini API error (${ev.errorCode ?? "unknown"}): ${ev.errorMessage ?? "no details"}`,
        );
      }

      if (sdk.isFinalResponse(event) && event.content) {
        const text = sdk.stringifyContent(event) || extractEventText(event);
        if (text) finalText = text;
      }
    }

    return finalText;
  }

  private nextMemoryRef(): MemoryRef {
    return {
      id: `adk-run-${++this.runCounter}-${Date.now()}`,
      provider: "google-adk",
      compat: `google-adk@${this.model}`,
      created_at: new Date().toISOString(),
    };
  }

  // -------------------------------------------------------------------------
  // SdkAdapter interface
  // -------------------------------------------------------------------------

  async send(prompt: string, options: AdapterSendOptions): Promise<string> {
    this.lastMemoryRef = null;

    const split = options.splitPrompt;
    let instruction = "";
    let userMessage: string;

    if (split && this.cacheConfig.enabled !== false) {
      instruction = split.system.join("\n\n---\n\n");
      userMessage = split.user;
    } else {
      userMessage = prompt;
    }

    const text = await this.runAgent(instruction, userMessage, options.agents, options.onProgress);
    this.lastMemoryRef = this.nextMemoryRef();
    return text;
  }

  async sendExecution(request: AgentExecutionRequest): Promise<string> {
    this.lastMemoryRef = null;

    const split = request.splitPrompt;
    let instruction = "";
    let userMessage: string;

    if (split && this.cacheConfig.enabled !== false) {
      instruction = split.system.join("\n\n---\n\n");
      userMessage = split.user;
    } else {
      userMessage = request.prompt;
    }

    const agents = request.options.agents ?? request.agents;
    const text = await this.runAgent(instruction, userMessage, agents, request.options.onProgress);
    this.lastMemoryRef = this.nextMemoryRef();
    return text;
  }

  getLastMemoryRef(): MemoryRef | null {
    return this.lastMemoryRef;
  }

  isCompatible(compat: string): boolean {
    return compat.startsWith("google-adk@");
  }

  // -------------------------------------------------------------------------
  // Test support
  // -------------------------------------------------------------------------

  /**
   * Inject the resolved ADK SDK surface for testing.
   * Bypasses the dynamic import of @google/adk and the API key requirement.
   */
  static withSdk(sdk: AdkSdk, config?: AdkSdkAdapterConfig): AdkSdkAdapter {
    const adapter = new AdkSdkAdapter(config);
    adapter.sdk = sdk;
    return adapter;
  }
}

// ---------------------------------------------------------------------------
// Progress event mapping
// ---------------------------------------------------------------------------

function emitProgressFromAdkEvent(
  event: AdkEvent,
  onProgress: (event: AgentProgressEvent) => void,
): void {
  const parts = event.content?.parts;
  if (!parts) return;
  for (const part of parts) {
    if (part.functionCall?.name) {
      onProgress({ type: "tool_use", tool_name: part.functionCall.name, input: part.functionCall.args });
    } else if (part.functionResponse?.name) {
      onProgress({ type: "tool_result", tool_name: part.functionResponse.name });
    } else if (part.text) {
      onProgress({ type: "text", message: part.text });
    }
  }
}
